import { API_CONFIG, buildApiUrl } from "../config/environment";

class FavoritesService {
  /**
   * Obtener favoritos del usuario con paginación
   */
  async getUserFavorites({ token, page = 1, limit = 10, search = "" } = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
      });

      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES
      );

      const response = await fetch(`${url}?${params}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error al obtener favoritos");
      }

      return {
        success: true,
        data: {
          favorites: data.data || [],
          pagination: data.pagination || {},
          meta: data.meta || {},
        },
      };
    } catch (error) {
      console.error("❌ Error fetching user favorites:", error);
      return {
        success: false,
        error: error.message,
        data: { favorites: [], pagination: {}, meta: {} },
      };
    }
  }

  /**
   * Agregar producto a favoritos
   */
  async addToFavorites({ productId, notes = "", token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product_id: productId,
          notes,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error al agregar favorito");
      }

      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    } catch (error) {
      console.error("❌ Error adding to favorites:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Eliminar producto de favoritos
   */
  async removeFromFavorites({ productId, token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES,
        { productId }
      );

      const response = await fetch(`${url}/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error al eliminar favorito");
      }

      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      console.error("❌ Error removing from favorites:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Verificar si un producto está en favoritos
   */
  async checkIsFavorite({ productId, token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES_CHECK,
        { productId }
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error al verificar favorito");
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("❌ Error checking favorite:", error);
      return {
        success: false,
        error: error.message,
        data: { is_favorite: false },
      };
    }
  }

  /**
   * Toggle favorito (agregar/remover)
   */
  async toggleFavorite(productId, token, notes = "") {
    try {
      // Primero verificar si ya es favorito
      const checkResult = await this.checkIsFavorite({ productId, token });

      if (!checkResult.success) {
        throw new Error("Error al verificar estado del favorito");
      }

      const isFavorite = checkResult.data.is_favorite;

      if (isFavorite) {
        // Remover de favoritos
        const result = await this.removeFromFavorites({ productId, token });
        return {
          ...result,
          action: "removed",
          isFavorite: false,
        };
      } else {
        // Agregar a favoritos
        const result = await this.addToFavorites({ productId, notes, token });
        return {
          ...result,
          action: "added",
          isFavorite: true,
        };
      }
    } catch (error) {
      console.error("❌ Error toggling favorite:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Verificar múltiples productos (batch)
   */
  async batchCheckFavorites({ productIds, token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES_BATCH_CHECK
      );

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ productIds }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error en verificación batch");
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("❌ Error batch checking favorites:", error);
      return {
        success: false,
        error: error.message,
        data: {},
      };
    }
  }

  /**
   * Eliminar múltiples productos de favoritos (bulk delete)
   */
  async bulkRemoveFromFavorites({ productIds, token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES_BULK
      );

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ product_ids: productIds }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error?.message || "Error al eliminar favoritos masivamente"
        );
      }

      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    } catch (error) {
      console.error("❌ Error bulk removing favorites:", error);
      return {
        success: false,
        error: error.message,
        data: { removedCount: 0, notFoundCount: 0, totalRequested: 0 },
      };
    }
  }

  /**
   * Obtener estadísticas de favoritos
   */
  async getFavoritesStats({ token }) {
    try {
      const url = buildApiUrl(
        API_CONFIG.BIENES_URL,
        API_CONFIG.ENDPOINTS.FAVORITES_STATS
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || "Error al obtener estadísticas");
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      console.error("❌ Error getting favorites stats:", error);
      return {
        success: false,
        error: error.message,
        data: { totalFavorites: 0, recentFavorites: 0 },
      };
    }
  }
}

export const favoritesService = new FavoritesService();
